// Sound effects utility for game interactions
const soundCache = {};

// Preload sound to improve performance
const preloadSound = (name, url) => {
  if (!soundCache[name]) {
    const audio = new Audio(url);
    soundCache[name] = audio;
    
    // Optional: preload the audio file
    audio.load();
  }
  return soundCache[name];
};

// Play a sound effect with volume control
const playSound = (name, options = {}) => {
  const { volume = 0.5, stopOthers = false } = options;

  if (!soundCache[name]) {
    console.warn(`Sound "${name}" not preloaded`);
    return;
  }

  if (stopOthers) {
    // Stop all other sounds
    Object.values(soundCache).forEach(audio => {
      if (audio !== soundCache[name] && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
  }

  // Reset the audio to start
  soundCache[name].currentTime = 0;
  soundCache[name].volume = volume;

  // Play the sound
  const playPromise = soundCache[name].play();

  // Handle potential play() Promise rejection (happens in some browsers)
  if (playPromise !== undefined) {
    playPromise.catch(error => {
      console.warn(`Error playing sound "${name}":`, error);
    });
  }
};

// Initialize all game sounds
const initSounds = () => {
  // Card and dice game sounds - softer, high quality sounds
  preloadSound('cardFlip', 'https://assets.mixkit.co/active_storage/sfx/2001/2001-preview.mp3');
  preloadSound('cardShuffle', 'https://assets.mixkit.co/active_storage/sfx/2004/2004-preview.mp3');
  preloadSound('diceRoll', 'https://assets.mixkit.co/active_storage/sfx/181/181-preview.mp3');
  
  // UI interaction sounds - subtle and professional
  preloadSound('click', 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3');
  preloadSound('navigationClick', 'https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3');
  preloadSound('hover', 'https://assets.mixkit.co/active_storage/sfx/1705/1705-preview.mp3');
  preloadSound('success', 'https://assets.mixkit.co/active_storage/sfx/220/220-preview.mp3');
  preloadSound('error', 'https://assets.mixkit.co/active_storage/sfx/1021/1021-preview.mp3');
  preloadSound('notification', 'https://assets.mixkit.co/active_storage/sfx/1518/1518-preview.mp3');
  
  // Game-specific sounds - clean and professional
  preloadSound('timerTick', 'https://assets.mixkit.co/active_storage/sfx/1529/1529-preview.mp3');
  preloadSound('timerEnd', 'https://assets.mixkit.co/active_storage/sfx/1529/1529-preview.mp3');
  preloadSound('levelUp', 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3');
  preloadSound('win', 'https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3');
  preloadSound('lose', 'https://assets.mixkit.co/active_storage/sfx/988/988-preview.mp3');
};

// Sound context provider to manage sound state (on/off)
const isSoundEnabled = () => {
  const soundSetting = localStorage.getItem('soundEnabled');
  return soundSetting === null ? true : soundSetting === 'true';
};

const toggleSound = () => {
  const newState = !isSoundEnabled();
  localStorage.setItem('soundEnabled', newState);
  return newState;
};

const playSoundIfEnabled = (name, options = {}) => {
  if (isSoundEnabled()) {
    playSound(name, options);
  }
};

export {
  initSounds,
  playSound,
  preloadSound,
  isSoundEnabled,
  toggleSound,
  playSoundIfEnabled
};